import React, { useState } from 'react';
import axios from 'axios';
import { Wand2, Plus, Clock, Coffee, Zap, Utensils, Activity, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_MAP = {
  focus: { icon: Zap, color: 'bg-[#A2D2FF]', label: 'Fokus' },
  break: { icon: Coffee, color: 'bg-[#FDF0D5]', label: 'Paus' },
  lunch: { icon: Utensils, color: 'bg-[#FFC8DD]', label: 'Lunch' },
  movement: { icon: Activity, color: 'bg-[#B9FBC0]', label: 'Rörelse' },
  admin: { icon: FileText, color: 'bg-[#CDB4DB]', label: 'Admin' },
  buffer: { icon: Clock, color: 'bg-white', label: 'Buffert' },
};

export default function SmartSchedule() {
  const [loading, setLoading] = useState(false);
  const [addingIdx, setAddingIdx] = useState(null);
  const [result, setResult] = useState(null);
  const [startTime, setStartTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:00`;
  });
  const [endTime, setEndTime] = useState('18:00');

  const suggest = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/schedule/suggest`,
        { start_time: startTime, end_time: endTime },
        { withCredentials: true }
      );
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte generera schema');
    } finally {
      setLoading(false);
    }
  };

  const addBlockToSchedule = async (block, idx) => {
    setAddingIdx(idx);
    try {
      await axios.post(
        `${API}/schedule`,
        {
          title: block.title,
          description: block.note || '',
          scheduled_time: block.start,
          days: ['everyday'],
          is_blocking: false,
          category: block.kind === 'focus' ? 'chores' : block.kind === 'lunch' ? 'self_care' : 'custom',
          points: 10,
        },
        { withCredentials: true }
      );
      toast.success(`${block.title} tillagd i schemat ✨`);
    } catch (e) {
      toast.error('Kunde inte lägga till');
    } finally {
      setAddingIdx(null);
    }
  };

  const addAllBlocks = async () => {
    if (!result?.blocks) return;
    for (let i = 0; i < result.blocks.length; i++) {
      await addBlockToSchedule(result.blocks[i], i);
    }
    toast.success('Alla block tillagda 🌸');
  };

  return (
    <div className="neo-card p-5 bg-white dark:bg-[#252545]" data-testid="smart-schedule">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2">
          <Wand2 className="w-5 h-5" /> Smart schema-förslag
        </h3>
      </div>
      <p className="text-xs opacity-75 mb-3">
        AI:n tittar på dina öppna tasks + focus-historik och bygger en realistisk dagsplan med fokus- och pauspass 🌷
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="flex items-center gap-1 text-xs">
          <span className="font-bold">Start</span>
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="px-2 py-1 rounded-lg border-2 border-black text-xs bg-white dark:bg-[#2d2d54]"
            data-testid="smart-schedule-start"
          />
        </label>
        <label className="flex items-center gap-1 text-xs">
          <span className="font-bold">Slut</span>
          <input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="px-2 py-1 rounded-lg border-2 border-black text-xs bg-white dark:bg-[#2d2d54]"
            data-testid="smart-schedule-end"
          />
        </label>
        <button
          onClick={suggest}
          disabled={loading}
          className="neo-btn px-3 py-1.5 bg-[#CDB4DB] text-sm font-bold flex items-center gap-1 disabled:opacity-50"
          data-testid="smart-schedule-suggest"
        >
          <Wand2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Tänker…' : 'Föreslå mitt schema'}
        </button>
      </div>

      {result && (
        <div className="space-y-2" data-testid="smart-schedule-result">
          {result.summary && (
            <div className="rounded-xl border-2 border-black p-3 bg-[#FDF0D5]">
              <p className="text-sm italic">💫 {result.summary}</p>
            </div>
          )}
          {result.stats && (
            <p className="text-[11px] opacity-60 pl-1">
              Senaste 7 dagar: {result.stats.avg_focus_minutes_7d} min/dag · {result.stats.sessions_7d} sessioner · {result.stats.open_tasks} öppna tasks
            </p>
          )}

          {result.blocks?.length > 0 && (
            <>
              {result.blocks.map((b, i) => {
                const cfg = KIND_MAP[b.kind] || KIND_MAP.focus;
                const Icon = cfg.icon;
                return (
                  <div
                    key={i}
                    className={`rounded-xl border-2 border-black p-3 ${cfg.color}`}
                    data-testid={`smart-schedule-block-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="text-[11px] font-bold flex-shrink-0 w-20">
                        <p>{b.start}</p>
                        <p className="opacity-60">→ {b.end}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <Icon className="w-3 h-3" />
                          <p className="font-bold text-sm">{b.title}</p>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white border-2 border-black">
                            {cfg.label}
                          </span>
                        </div>
                        {b.note && <p className="text-xs mt-1 italic opacity-80">{b.note}</p>}
                      </div>
                      <button
                        onClick={() => addBlockToSchedule(b, i)}
                        disabled={addingIdx === i}
                        className="neo-btn p-1.5 bg-white hover:bg-[#B9FBC0] disabled:opacity-50"
                        title="Lägg till i schemat"
                        data-testid={`smart-schedule-add-${i}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
              <button
                onClick={addAllBlocks}
                className="neo-btn w-full py-2 bg-[#B9FBC0] text-sm font-bold"
                data-testid="smart-schedule-add-all"
              >
                ✨ Lägg till alla i schemat
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
